/**
 * summonGate — does this message call for Prest0n?
 *
 * Owner directive 2026-08-06: "like in Slack, there should be a toggle in the
 * thread settings … '@Prest0n mention required for summoning Prest0n or not'.
 * The default upon creating a new account or existing accounts should be 'Yes'."
 * With the toggle on, Prest0n answers only when explicitly summoned. Users can
 * also flip it per thread by telling Prest0n in plain language.
 *
 * THE ONE-ON-ONE CARVE-OUT. The same directive adds: "Prest0n should still
 * respond to direct conversational messages in a dedicated one-on-one chat
 * without an @Prest0n mention." Both halves hold together under a single rule —
 * the requirement suppresses a reply only where a thread has TWO OR MORE humans.
 * In a 1:1 the setting still exists and still reads Yes; it simply never
 * suppresses anything, because there is no ambiguity about who is being
 * addressed. A toggle that silenced Prest0n in its own dedicated chat would make
 * the product unusable, and that is plainly not what was asked for.
 *
 * Fail-OPEN by construction. Every uncertain input — unknown participant count,
 * missing setting, unreadable membership — resolves to "summon". A thread where
 * Prest0n has gone quiet for an unexplainable reason is far worse than one where
 * it answered when it needn't have, and the group-threading read path already
 * fails closed to an empty membership snapshot on any error.
 *
 * Pure and dependency-free so the rule is unit-tested without React or Firestore.
 */
import { hasMention, parseMentionUids } from './mentionParser';

/** The assistant's addressable handles, lowercase. Mirrors the reserved-name set. */
const ASSISTANT_HANDLES: ReadonlySet<string> = new Set([
  'prest0n',
  'preston',
  'prest0n-ai',
  'preston-ai',
]);

/**
 * Does this bare `@handle` address Prest0n? (Miles, 2026-09-22: "when someone
 * mentions Prest0n it should do the same thing.")
 *
 * Exported because the mention CHIP has to ask the same question this file
 * already answers for the summon. Prest0n is not a roster member, so the chip's
 * human resolver — `resolveMentionsToUids` over the thread's participants —
 * returns nothing for him: `@Prest0n` was drawn as a handle that had missed,
 * which is the exact inversion of what the chip is for, on the one handle in a
 * gated room that always lands.
 *
 * It must be THIS set and not a second copy of it. A chip that confirmed a
 * spelling this gate does not recognise would be a confirmation of nothing.
 *
 * Trailing `.`/`_` are stripped for the same reason `mentionsAssistant` strips
 * them: "@Prest0n." ends a sentence, it does not name somebody else.
 */
export function handleAddressesAssistant(handle: string | null | undefined): boolean {
  const key = String(handle ?? '').trim().toLowerCase().replace(/[._]+$/, '');
  return key.length > 0 && ASSISTANT_HANDLES.has(key);
}

/** Sentinel senderId the shared store uses for assistant turns. */
const ASSISTANT_SENDER_ID = 'assistant';

/** `@handle` written in plain text — what a human actually types. */
const PLAIN_AT_MENTION = /(^|[^\w@])@([A-Za-z0-9][A-Za-z0-9._-]{0,63})/g;

/**
 * True when the text addresses Prest0n — either as a plain `@Prest0n`, or as the
 * composer's `<@uid>` token when that uid is the assistant sentinel.
 */
export function mentionsAssistant(text: string | null | undefined): boolean {
  const value = typeof text === 'string' ? text : '';
  if (!value.trim()) return false;

  PLAIN_AT_MENTION.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PLAIN_AT_MENTION.exec(value)) !== null) {
    if (handleAddressesAssistant(m[2])) return true;
  }

  if (hasMention(value)) {
    for (const uid of parseMentionUids(value)) {
      if (uid.trim().toLowerCase() === ASSISTANT_SENDER_ID) return true;
    }
  }
  return false;
}

/**
 * True when the message is ONLY a summons — one or more assistant mentions
 * with nothing left to answer once they are removed (whitespace and
 * punctuation aside).
 *
 * Why this exists (INCIDENT ref fern-msjhf1xu, 2026-08-07 21:53 UTC): a bare
 * "@Prest0n" summons a dedicated-VM turn whose ENTIRE user request is the
 * mention. The agent is disciplined to answer only the user request, which
 * contains nothing to answer, so it produces empty output; the VM adapter's
 * empty-response guard turns that into HTTP 502 → the misleading "returned an
 * unexpected response" (FERN-ROUTE-002) card — and Regenerate re-runs the same
 * contentless prompt, so it can loop. The composer refuses the send up front
 * instead, with a hint to add the actual question.
 *
 * Mentions of OTHER people do not count as content ("@Prest0n @Malaika" is
 * still a contentless summons), but any real word does.
 */
export function isAssistantMentionOnly(text: string | null | undefined): boolean {
  const value = typeof text === 'string' ? text : '';
  if (!value.trim()) return false;
  if (!mentionsAssistant(value)) return false;

  // Remove every plain-text @mention (assistant or human — a list of names
  // with no ask is still nothing to answer)…
  let residue = value.replace(PLAIN_AT_MENTION, (full, pre: string) => pre ?? '');
  // …and any composer `<@uid>` tokens.
  residue = residue.replace(/<@[^>\s]+>/g, '');
  // Whitespace/punctuation left over does not make it answerable.
  return residue.replace(/[\s.,;:!?¡¿…()\-–—]+/g, '') === '';
}

export type SummonReason =
  /** One-on-one with Prest0n — every message is addressed to it. */
  | 'direct-chat'
  /** The thread does not require a mention. */
  | 'mention-not-required'
  /** The thread requires a mention and got one. */
  | 'mentioned'
  /** The thread requires a mention and did not get one — stay quiet. */
  | 'not-mentioned'
  /** A permitted user took Prest0n OUT of this thread (2026-09-08) — never summon. */
  | 'assistant-removed';

export interface SummonDecision {
  summon: boolean;
  reason: SummonReason;
}

export interface SummonGateArgs {
  text: string | null | undefined;
  /**
   * The thread's setting. Absent/unknown ⇒ treated as REQUIRED, matching the
   * stated default of "Yes" for new and existing threads alike — but see the
   * participant rule below, which is what keeps that default safe to ship.
   */
  requireMention?: boolean | null;
  /**
   * How many HUMANS are in the thread (Prest0n is not counted). Absent or < 2 ⇒
   * treated as a direct chat.
   */
  humanParticipantCount?: number | null;
  /**
   * The thread's stored "Prest0n is in this thread" fact (2026-09-08). ONLY an
   * explicit `false` (a permitted user removed Prest0n) silences a send — a
   * missing or malformed value reads as "in", the default for every thread.
   * Applies in a one-on-one too: a solo thread without Prest0n is a notes
   * thread, and its owner asked for exactly that.
   */
  assistantEnabled?: boolean | null;
}

/**
 * Decide whether this message should summon Prest0n.
 *
 * The `reason` is returned so callers can explain the outcome rather than a
 * silent no-op — a Prest0n that goes quiet without saying why is exactly the
 * failure mode this feature could introduce.
 */
export function resolveSummonDecision(args: SummonGateArgs): SummonDecision {
  if (args.assistantEnabled === false) return { summon: false, reason: 'assistant-removed' };
  const humans = Number(args.humanParticipantCount);
  const isGroup = Number.isFinite(humans) && humans >= 2;
  if (!isGroup) return { summon: true, reason: 'direct-chat' };

  // Absent ⇒ required (the stated default for existing threads).
  const required = args.requireMention !== false;
  if (!required) return { summon: true, reason: 'mention-not-required' };

  return mentionsAssistant(args.text)
    ? { summon: true, reason: 'mentioned' }
    : { summon: false, reason: 'not-mentioned' };
}

/** Convenience predicate for call sites that don't need the reason. */
export function shouldSummonAssistant(args: SummonGateArgs): boolean {
  return resolveSummonDecision(args).summon;
}

// ─────────────────────────────────────────────────────────────────────────────
// Plain-language control
//
// "Users should be able to simply inform Prest0n in plain language and Prest0n
// should be able to toggle this on or off accordingly on a thread-by-thread
// basis." This is the deterministic pre-parse: it recognises only unambiguous
// instructions, so an ordinary sentence that happens to mention being quiet
// never silently reconfigures a thread. Anything it does not recognise returns
// null and falls through to Prest0n's own tool call.
// ─────────────────────────────────────────────────────────────────────────────

const REQUIRE_ON = [
  /only\s+(respond|reply|answer|speak)\s+when\s+(i\s+)?(@?prest0n\s+is\s+)?mention/i,
  /only\s+(respond|reply|answer|speak)\s+(to\s+me\s+)?(if|when)\s+(i\s+)?(mention|tag|@)/i,
  /(don'?t|do not|stop)\s+(respond|reply|answer|speak|chime|jump)\w*\s+(unless|until)\s+(i\s+)?(mention|tag|@)/i,
  /require\s+(an?\s+)?@?\s*(prest0n\s+)?mention/i,
  /mention[- ]only\s+mode/i,
];

const REQUIRE_OFF = [
  /(respond|reply|answer|speak|chime)\w*\s+(to\s+)?(every|all|each)\s+(message|thing)/i,
  /(you\s+)?(don'?t|do not)\s+need\s+(to\s+be\s+)?(a\s+)?mention/i,
  /(respond|reply|answer)\s+without\s+(being\s+)?(mention|tag)/i,
  /(turn|switch)\s+off\s+(the\s+)?mention[- ]only/i,
  /stop\s+(requiring|needing)\s+(an?\s+)?@?\s*(prest0n\s+)?mention/i,
];

/**
 * Parse an explicit instruction to change this thread's summon setting.
 * Returns `true` (mention now required), `false` (no longer required), or null
 * when the message is not an unambiguous instruction.
 */
export function parseSummonSettingInstruction(text: string | null | undefined): boolean | null {
  const value = typeof text === 'string' ? text.trim() : '';
  if (!value) return null;
  // Check OFF first: "you don't need to be mentioned" also matches loosely
  // against some ON patterns, and the explicit relaxation is the safer read.
  for (const re of REQUIRE_OFF) if (re.test(value)) return false;
  for (const re of REQUIRE_ON) if (re.test(value)) return true;
  return null;
}
